import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getAlerts, updateAlertStatus, runMonitor, getMe } from '../lib/api'
import AlertCard from '../components/AlertCard'
import { RefreshCw } from 'lucide-react'

const STATUSES = ['active', 'dismissed', 'acted_on', 'snoozed']

export default function Alerts() {
  const [status, setStatus] = useState('active')
  const qc = useQueryClient()

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => getMe().then(r => r.data) })
  const personId = me?.id

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['alerts', status, personId],
    queryFn: () => getAlerts(personId!, status).then((r) => r.data),
    enabled: !!personId,
  })

  const runMon = useMutation({
    mutationFn: runMonitor,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, s }: { id: string; s: string }) => updateAlertStatus(id, s),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  })

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Alerts</h2>
          <p className="text-sm text-gray-500">Health alerts derived from your atom history</p>
        </div>
        <button
          onClick={() => runMon.mutate()}
          disabled={runMon.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 self-start sm:self-auto"
        >
          <RefreshCw size={14} className={runMon.isPending ? 'animate-spin' : ''} />
          Check now
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              status === s
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {!isLoading && alerts.length === 0 && (
        <p className="text-sm text-gray-400">No {status} alerts.</p>
      )}
      <div className="space-y-3">
        {alerts.map((a: Parameters<typeof AlertCard>[0]['alert']) => (
          <AlertCard
            key={a.id}
            alert={a}
            onDismiss={status === 'active' ? (id) => changeStatus.mutate({ id, s: 'dismissed' }) : undefined}
            onActedOn={status === 'active' ? (id) => changeStatus.mutate({ id, s: 'acted_on' }) : undefined}
          />
        ))}
      </div>
    </div>
  )
}
